import { Injectable } from '@angular/core';
import { HttpApiService } from '../http-api/http-api.service';
import { LoginService } from 'src/app/accountancy/authentication/login/login-service/login.service';
import { HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { AccChartOfAccountCategoryInterface, AccChartOfAccountInterface, RateInterface, ClientDetailedInformationInterface, ApiGetResponse, ApiPostResponse, ApiPutResponse, DetachmentInterface, GeneralLedgerInterface, TrainingAPInterface, BuiltBatchDateInterface, PayslipsBuiltByBatchDateInterface, PayslipsDetachmentBuiltByBatchDateInterface, ApiDeleteResponse, ApiPatchResponse } from '../../interfaces/global.interface';
import { Helper } from '../../classes/helper.class';
import { BillingByBatchInterface, BillingStatementInterface } from '../../interfaces/billing-statement.interface';
import { AccountPayableAssetInterface, AccountPayableInterface, AccountPayableModifiedInterface } from '../../interfaces/account-payable.interface';
import { map, of } from 'rxjs';
import { UserInterface } from '../../interfaces/user-data.interface';
import { PrintedSalesInvoiceInterface, SalesInvoiceCancellationRequestInterface, SalesInvoiceStatus } from '../../interfaces/sales-invoice.interface';

@Injectable({
  providedIn: 'root'
})
export class AccountingApiService {


  private readonly _sendRequest = {
    sendGetRequest: <T>(url: string, onError?: ((err?: any | null) => void) | null) => this._api.sendGetRequest<T>(url, this._loginS.getTokenHeader, new HttpParams(), onError),
    sendDeleteRequest: <T>(url: string, onError?: ((err?: any | null) => void) | null) => this._api.sendDeleteRequest<T>(url, this._loginS.getTokenHeader, new HttpParams(), onError),
    sendPutRequest: <T>(url: string, body: Object, onError?: ((err?: any | null) => void) | null) => this._api.sendPutRequest<T>(url, body, this._loginS.getTokenHeader, new HttpParams(), onError),
    sendPostRequest: <T>(url: string, body: Object, onError?: ((err?: any | null) => void) | null) => this._api.sendPostRequest<T>(url, body, this._loginS.getTokenHeader, new HttpParams(), onError),
    sendPatchRequest: <T>(url: string, body: Object, onError?: ((err?: any | null) => void) | null) => this._api.sendPatchRequest<T>(url, body, this._loginS.getTokenHeader, new HttpParams(), onError),

    hris: this._api.hris,
  };

  constructor(
    private _api: HttpApiService,
    private _loginS: LoginService,
  ) { }

  public readonly generatedSalesInvoice = {
    get: (
      params: {
        start?: number | null,
        limit?: number | null,
        /** Number Ref, client name, SI / batch / BS number. */
        search?: string | null,
        batchId?: number | null,
        clientId?: number | null,
        /** Only invoices that include this billing statement. */
        billingId?: number | null,
        status?: SalesInvoiceStatus | null,
      },
      onError?: ((err?: any | null) => void) | null
    ) => this._sendRequest
      .sendGetRequest<HttpResponse<ApiGetResponse<PrintedSalesInvoiceInterface> & { can_decide_cancellation?: boolean }>>(
        Helper.url.toParams('/api/v1/generated-sales-invoice', params),
        onError
      ),

    new: (body: Object, onError?: ((err?: any | null) => void) | null) => this._sendRequest
      .sendPostRequest<HttpResponse<{ lastrowid: number }>>(
        `/api/v1/generated-sales-invoice`, body, onError
      ),

    /** Cancellation requests of an invoice. Remarks / reason are required by the BE for every step. */
    cancellation: {
      /** History of one invoice, newest first, plus whether the logged-in user is a cancellation approver. */
      history: (siId: number, onError?: ((err?: any | null) => void) | null) => this._sendRequest
        .sendGetRequest<HttpResponse<{ si_id: number, si_status: SalesInvoiceStatus, can_decide: boolean, data: SalesInvoiceCancellationRequestInterface[] }>>(
          Helper.url.toParams('/api/v1/generated-sales-invoice', { historyOfSi: siId }), onError
        ),

      request: (body: { si_id: number, reason: string }, onError?: ((err?: any | null) => void) | null) => this._sendRequest
        .sendPutRequest<HttpResponse<{ request_id: number }>>(`/api/v1/generated-sales-invoice`, { action: 'request', ...body }, onError),

      decide: (body: { request_id: number, decision: 'Approved' | 'Disapproved', remarks: string }, onError?: ((err?: any | null) => void) | null) => this._sendRequest
        .sendPutRequest<HttpResponse<{ request_id: number, si_status: SalesInvoiceStatus }>>(`/api/v1/generated-sales-invoice`, { action: 'decide', ...body }, onError),
    },
  }

  public readonly trainingAP = {
    gellAll: (start?: number | null, limit?: number | null, query?: string | null, other?: Object | null, onError?: ((err?: any | null) => void) | null) => this._sendRequest
      .sendGetRequest<HttpResponse<ApiGetResponse<TrainingAPInterface>>>(
        Helper.url.toParams(`/api/v1/training-events/no-payment-details`, {
          Start: start,
          Limit: limit,
          Search: query,
          ...other
        }), onError
      ),

    updatePaymentDetails: (trainingId: number, body: Object, onError?: ((err?: any | null) => void) | null) => this._sendRequest
      .sendPutRequest(`/api/v1/training-event/${trainingId}/payment-details`, body, onError),
  }

  public readonly address = {
    getCities: (onError?: ((err?: any | null) => void) | null) => this._sendRequest.hris
      .sendGetRequest<HttpResponse<any>>(`/cities`),
  }

  public readonly dashboard = {
    getBirthdays: (onError?: ((err?: any | null) => void) | null) => this._sendRequest.hris
      .sendGetRequest<HttpResponse<any>>(`/birthday-celebrants`),
  }

  public readonly approvalSettings = {
    get: (
      params: {
        start?: number | null,
        limit?: number | null,
        Transac_Type?: string | null
      },
      onError?: ((err?: any | null) => void) | null
    ) => this._sendRequest
      .sendGetRequest<HttpResponse<ApiGetResponse<any>>>(
        Helper.url.toParams('/api/v2/approval-settings', params),
        onError
      ),
  }

  public readonly billing = {

    update: (billing_id: number, body: Object) => this._sendRequest
      .sendPatchRequest<HttpResponse<ApiPatchResponse<any>>>(`/api/v1/update-billing?BillingId=${billing_id}`, body),

    get: (
      params: {
        start?: number | null,
        limit?: number | null,
        BillingId?: number | null
        Clients?: string,
        Detachments?: string,
        Areas?: string,
      },
      onError?: ((err?: any | null) => void) | null
    ) => this._sendRequest
      .sendGetRequest<HttpResponse<ApiGetResponse<BillingStatementInterface>>>(
        Helper.url.toParams('/api/v2/billing-statements', params),
        onError
      ),

    getAllBatch: (
      params: {
        start?: number | null,
        limit?: number | null,
        BillingBatch?: string | null
      },
      onError?: ((err?: any | null) => void) | null
    ) => this._sendRequest
      .sendGetRequest<HttpResponse<ApiGetResponse<BillingByBatchInterface>>>(
        Helper.url.toParams('/api/v2/billing-statements-batches', params),
        onError
      ),

    // getById: (billingId: number, onError?: ((err?: any | null) => void) | null) => this._sendRequest
    //   .sendGetRequest<HttpResponse<{ data: BillingStatementInterface }>>(`/api/v1/billing-statement/${billingId}`, onError),

    generate: (body: object, onError?: ((err?: any | null) => void) | null) => this._sendRequest
      .sendPostRequest<HttpResponse<{ message?: string | null, data?: BillingStatementInterface[] | null }>>(`/api/v1/create-billing`, body, onError),


    updateGeneratedById: (billingId: number, body: Object, onError?: ((err?: any | null) => void) | null) => this._sendRequest
      .sendPutRequest(`/api/v1/billing-statement/${billingId}`, body, onError),

  }

  public readonly generalLedger = {

    gellAll: (start?: number | null, limit?: number | null, query?: string | null, other?: Object | null, onError?: ((err?: any | null) => void) | null) => this._sendRequest
      .sendGetRequest<HttpResponse<ApiGetResponse<GeneralLedgerInterface>>>(
        Helper.url.toParams(`/api/v1/general-ledger`, {
          Start: start,
          Limit: limit,
          Search: query,
          ...other,
        }), onError
      )
  }

  public readonly clients = {

    newClient: (body: Object, onError?: ((err?: any | null) => void) | null) => this._sendRequest
      .sendPostRequest<HttpResponse<ApiPostResponse<ClientDetailedInformationInterface>>>(
        `/api/v1/client`, body, onError
      ),

    updateClientById: (clientId: number, body: Object, onError?: ((err?: any | null) => void) | null) => this._sendRequest
      .sendPutRequest(
        `/api/v1/client/${clientId}`, body, onError
      ),

    deleteClient: (clientId: number, onError?: ((err?: any | null) => void) | null) => this._sendRequest
      .sendDeleteRequest<HttpResponse<ApiDeleteResponse<ClientDetailedInformationInterface>>>(
        `/api/v1/client/${clientId}`, onError
      ),




    getClients: (
      params: {
        start?: number | null,
        limit?: number | null,
        Name?: string | null,
        Id?: number | null
      },
      onError?: ((err?: any | null) => void) | null
    ) => this._sendRequest
      .sendGetRequest<HttpResponse<ApiGetResponse<ClientDetailedInformationInterface>>>(
        Helper.url.toParams('/api/v2/clients', params),
        onError
      ),




    getAllAvailableDetachments: (onError?: ((err?: any | null) => void) | null) => this._sendRequest
      .sendGetRequest<HttpResponse<ApiGetResponse<{ Id: number, Detachment: string }>>>(
        `/api/v2/areas/available-detachments`, onError
      ),

    getAllAvailableArea: (onError?: ((err?: any | null) => void) | null) => this._sendRequest
      .sendGetRequest<HttpResponse<ApiGetResponse<{ id: number, DetachmentName: string }>>>(
        `/api/v2/areas/available-areas`, onError
      ),

    addNewDetachmentToClient: (client_id: number, body: Object, onError?: ((err?: any | null) => void) | null) => this._sendRequest
      .sendPostRequest<HttpResponse<ApiPostResponse<RateInterface>>>(
        `/api/v2/areas`,
        {
          parent_id: null,
          client_id,
          ...body
        },
        onError
      ),

    updateClientDetachmentHRIS: (detachmentId: number, body: Object, onError?: ((err?: any | null) => void) | null) => this._sendRequest
      .sendPutRequest<HttpResponse<ApiPutResponse<RateInterface>>>(`/api/v1/detachment/${detachmentId}`, body, onError),

    area: {

      get: (
        params: {
          start?: number | null,
          limit?: number | null,
          parentId?: number | null,
          DetachmentName?: string | null,
          Id?: number | null
        },
        onError?: ((err?: any | null) => void) | null
      ) => this._sendRequest
        .sendGetRequest<HttpResponse<ApiGetResponse<RateInterface>>>(
          Helper.url.toParams('/api/v2/areas', params),
          onError
        ),

      new: (parent_id: number, body: Object, onError?: ((err?: any | null) => void) | null) => this._sendRequest
        .sendPostRequest<HttpResponse<ApiPostResponse<RateInterface>>>(
          `/api/v2/areas`,
          {
            client_id: null,
            parent_id,
            ...body,
          },
          onError
        ),

      update: (areaId: number, body: Object, onError?: ((err?: any | null) => void) | null) => this._sendRequest
        .sendPatchRequest<HttpResponse<ApiPatchResponse<any>>>(
          `/api/v2/areas`, { id: areaId, ...body }, onError
        ),

      delete: (clientId: number, areaId: number, onError?: ((err?: any | null) => void) | null) => this._sendRequest
        .sendDeleteRequest<HttpResponse<ApiDeleteResponse<RateInterface>>>(
          `/api/v2/areas?Id=${areaId}&client_Id=${clientId}`, onError
        ),

    }
  }

  public readonly chartOfAccounts = {

    getChartOfAccountList: (start?: number | null, limit?: number | null, query?: string | null, other?: Object | null, onError?: ((err?: any | null) => void) | null) => this._sendRequest
      .sendGetRequest<HttpResponse<ApiGetResponse<AccChartOfAccountInterface>>>(
        Helper.url.toParams(`/api/v1/chart-of-accounts`, {
          Start: start,
          Limit: limit,
          Search: query,
          ...other
        }), onError
      ),

    newChartOfAccount: (body: Object, onError?: ((err?: any | null) => void) | null) => this._sendRequest
      .sendPostRequest(`/api/v1/chart-account`, body, onError),

    updateChartOfAccount: (id: number, body: Object, onError?: ((err?: any | null) => void) | null) => this._sendRequest
      .sendPutRequest(`/api/v1/chart-account/${id}`, body, onError),

    deleteChartOfAccount: (id: number, onError?: ((err?: any | null) => void) | null) => this._sendRequest
      .sendDeleteRequest(`/api/v1/chart-account/${id}`, onError),

    category: {

      getCategoryList: (start?: number | null, limit?: number | null, query?: string | null, other?: Object | null, onError?: ((err?: any | null) => void) | null) => this._sendRequest
        .sendGetRequest<HttpResponse<ApiGetResponse<AccChartOfAccountCategoryInterface>>>(
          Helper.url.toParams(`/api/v1/accounts-categories`, {
            Start: start,
            Limit: limit,
            Search: query,
            ...other,
          }), onError
        ),

      newCategory: (body: Object, onError?: ((err?: any | null) => void) | null) => this._sendRequest
        .sendPostRequest(`/api/v1/accounts-category`, body, onError),

      updateCategory: (id: number, body: Object, onError?: ((err?: any | null) => void) | null) => this._sendRequest
        .sendPutRequest(`/api/v1/accounts-category/${id}`, body, onError),

      deleteCategory: (id: number, onError?: ((err?: any | null) => void) | null) => this._sendRequest
        .sendDeleteRequest(`/api/v1/accounts-category/${id}`, onError),
    }
  }

  public readonly acountReceivable = {

    regenerate: (id: number) => {
      return this._api.sendPutRequest<HttpResponse<ApiPutResponse<{}>>>(`/api/v1/billing-statement/${id}`, {
        recompute: true,
      }, this._loginS.getTokenHeader);
    },

    newAR: (body: Object, onError?: ((err?: any | null) => void) | null) => {
      return this._api.sendPostRequest<HttpResponse<ApiPostResponse<BillingStatementInterface>>>(
        `/api/v1/create-billing`, body, this._loginS.getTokenHeader, new HttpParams(), onError
      ).pipe(
        map(values => values.body),
      );
    },

    generateBlling: {
      fixed: (body: Object, onError?: ((err?: any | null) => void) | null) => this._sendRequest
        .sendPostRequest(`/api/v1/generate-billing-fixed`, body, onError),

      detailed: (body: Object, onError?: ((err?: any | null) => void) | null) => this._sendRequest
        .sendPostRequest(`/api/v1/generate-billing-detailed`, body, onError),

      custom: (body: Object, onError?: ((err?: any | null) => void) | null) => this._sendRequest
        .sendPostRequest(`/api/v1/generate-billing-custom`, body, onError),
    },

    updateAR: (id: number, body: Object, onError?: ((err?: any | null) => void) | null) => {
      return this._api.sendPutRequest<HttpResponse<ApiPostResponse<BillingStatementInterface>>>(
        `/api/v1/account-receivable/${id}`, body, this._loginS.getTokenHeader, new HttpParams(), onError
      ).pipe(
        map(values => values.body),
      );
    },

    updateStatus: (id: number, Status: string, Remarks: string, onError?: ((err?: any | null) => void) | null) => {
      return this._api.sendPutRequest<HttpResponse<ApiPostResponse<BillingStatementInterface>>>(
        `/api/v1/account-receivable/${id}/status`,
        { Status, Remarks },
        this._loginS.getTokenHeader, new HttpParams(), onError
      ).pipe(
        map(values => values.body),
      );
    },

    getBuiltBatchDates: (other?: Object | null, onError?: ((err?: any | null) => void) | null) => {
      return this._api.hris.sendGetRequest<HttpResponse<BuiltBatchDateInterface[]>>(
        Helper.url.toParams(`/payslips/batch-date`, {
          ...other,
        })
        //, this._loginS.getTokenHeader
        , new HttpHeaders()
        , new HttpParams(), onError
      );
    },

    getPayslipsByBatchDate: (buildDate: string, cutOffFrom: string, cutOffTo: string, other?: Object | null, onError?: ((err?: any | null) => void) | null) => {
      return this._api.hris.sendGetRequest<HttpResponse<PayslipsBuiltByBatchDateInterface>>(
        Helper.url.toParams(`/payslips`, {
          Date: buildDate,
          CutoffPeriodFrom: cutOffFrom,
          CutoffPeriodTo: cutOffTo,
          ...other,
        })
        //, this._loginS.getTokenHeader
        , new HttpHeaders()
        , new HttpParams(), onError
      );
    }
  }

  public readonly accountPayable = {

    getApAsset: (start?: number | null, limit?: number | null, query?: string | null, by?: number | null, approver?: number | null, other?: Object | null, onError?: ((err?: any | null) => void) | null) => {
      return this._api.sendGetRequest<HttpResponse<ApiGetResponse<AccountPayableAssetInterface>>>(
        Helper.url.toParams(`/api/v1/ap-assets/no-payment-details`, {
          Start: start,
          Limit: limit,
          Search: query,
          RequestedBy: by,
          Approver: approver,
          ...other
        }),
        this._loginS.getTokenHeader, new HttpParams(), onError
      );
    },

    updateApAsset: (prId: string, body: Object, onError?: ((err?: any | null) => void) | null) => {
      return this._api.sendPutRequest<HttpResponse<ApiPutResponse<AccountPayableAssetInterface>>>(
        `/api/v1/ap-asset/${prId}/payment-details`, body, this._loginS.getTokenHeader, new HttpParams(), onError
      ).pipe(
        map(values => values.body),
      );
    },

    getApWithPaymentDetails: (start?: number | null, limit?: number | null, query?: string | null, by?: number | null, approver?: number | null, other?: Object | null, onError?: ((err?: any | null) => void) | null) => {
      return this._api.sendGetRequest<HttpResponse<ApiGetResponse<AccountPayableInterface>>>(
        Helper.url.toParams(`/api/v1/account-payables`, {
          Start: start,
          Limit: limit,
          Search: query,
          RequestedBy: by,
          Approver: approver,
          ...other,
        }),
        this._loginS.getTokenHeader, new HttpParams(), onError
      );
    },

    newAp: (body: Object, onError?: ((err?: any | null) => void) | null) => {
      return this._api.sendPostRequest<HttpResponse<ApiPostResponse<AccountPayableInterface>>>(
        `/api/v1/account-payable`, body, this._loginS.getTokenHeader, new HttpParams(), onError
      ).pipe(
        map(values => values.body),
      );
    },

    updateAp: (apId: number, body: Object, onError?: ((err?: any | null) => void) | null) => {
      return this._api.sendPutRequest<HttpResponse<ApiPutResponse<AccountPayableInterface>>>(
        `/api/v1/account-payable/${apId}`, body, this._loginS.getTokenHeader, new HttpParams(), onError
      ).pipe(
        map(values => values.body),
      );
    },

    updateApChangeStatus: (apId: number, newStatus: string, onError?: ((err?: any | null) => void) | null) => {
      return this._api.sendPutRequest<HttpResponse<ApiPostResponse<AccountPayableInterface>>>(
        `/api/v1/account-payable/${apId}/status`, { Status: newStatus }, this._loginS.getTokenHeader, new HttpParams(), onError
      ).pipe(
        map(values => values.body)
      );
    },

    updateApMarkVoucherPrinted: (apId: number, onError?: ((err?: any | null) => void) | null) => {
      return this._api.sendPostRequest<HttpResponse<any>>(
        `/api/v1/account-payable/${apId}/voucher`, {}, this._loginS.getTokenHeader, new HttpParams(), onError
      ).pipe(
        map(values => values.body as { Account_Payable_Info: AccountPayableModifiedInterface, ReferenceNumber: string })
      );
    },

  }

  public readonly employee = {
    getAll: (Start?: number | null, Limit?: number | null, Search?: string | null, others?: Object | null, onError?: ((err?: any | null) => void) | null) => {
      return this._sendRequest.sendGetRequest<HttpResponse<ApiGetResponse<UserInterface>>>(Helper.url.toParams(
        `/api/v1/users`, {
        Start,
        Limit,
        Search,
        ...others
      }
      ), onError);
    }
  }





}
